/* eslint-disable unicorn/no-null */
import type { FC, FormEvent } from 'react';
import { useMemo, useState } from 'react';

import { Button, Select, UnstyledButton } from '@mantine/core';

import {
    useV3RegistriesQuery,
    useV3RegistryModulesQuery
} from './RegistryModules.queries';
import type {
    RegistryModuleFormProps,
    RegistryModuleSelection
} from './RegistryModuleForm.types';

const LATEST_VERSION_VALUE = '__latest__';

const RegistryModuleForm: FC<RegistryModuleFormProps> = ({
    allowLatestVersion = false,
    isSubmitDisabled = false,
    isPending,
    submitLabel,
    onCancel,
    onSubmit
}) => {
    const [selectedRegistryId, setSelectedRegistryId] = useState<
        string | undefined
    >();
    const [selectedModuleIdn, setSelectedModuleIdn] = useState<
        string | undefined
    >();
    const [selectedVersionId, setSelectedVersionId] = useState<
        string | undefined
    >();

    const { data: registries = [] } = useV3RegistriesQuery();
    const { data: modules = [] } =
        useV3RegistryModulesQuery(selectedRegistryId);

    const selectedRegistry = registries.find(
        (registry) => registry.id === selectedRegistryId
    );
    const isLatestVersion = selectedVersionId === LATEST_VERSION_VALUE;
    const selectedModule = isLatestVersion
        ? undefined
        : modules.find((module) => module.id === selectedVersionId);

    const registryOptions = useMemo(
        () =>
            registries.map((registry) => ({
                label: registry.idn,
                value: registry.id
            })),
        [registries]
    );

    const moduleOptions = useMemo(
        () =>
            Array.from(new Set(modules.map((module) => module.idn)), (idn) => ({
                value: idn,
                label: idn
            })),
        [modules]
    );

    const versionOptions = useMemo(() => {
        if (!selectedModuleIdn) {
            return [];
        }

        const exactVersions = modules
            .filter((module) => module.idn === selectedModuleIdn)
            .map((module) => ({
                value: module.id,
                label: module.version
            }));

        return allowLatestVersion
            ? [
                  { value: LATEST_VERSION_VALUE, label: 'Latest version' },
                  ...exactVersions
              ]
            : exactVersions;
    }, [allowLatestVersion, modules, selectedModuleIdn]);

    const selection: RegistryModuleSelection | null =
        selectedRegistry &&
        selectedModuleIdn &&
        (isLatestVersion || selectedModule)
            ? {
                  registryIdn: selectedRegistry.idn,
                  moduleIdn: selectedModuleIdn,
                  moduleVersion: selectedModule?.version ?? null,
                  projectVersionId: selectedModule?.project_version_id ?? null
              }
            : null;

    const handleRegistryChange = (value: string | null): void => {
        setSelectedRegistryId(value ?? undefined);
        setSelectedModuleIdn(undefined);
        setSelectedVersionId(undefined);
    };

    const handleModuleChange = (value: string | null): void => {
        setSelectedModuleIdn(value ?? undefined);
        setSelectedVersionId(undefined);
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        if (selection) {
            void onSubmit(selection);
        }
    };

    return (
        <form
            autoComplete="off"
            className="flex flex-col gap-3"
            onSubmit={handleSubmit}
        >
            <Select
                clearable
                withAsterisk
                comboboxProps={{ withinPortal: false }}
                data={registryOptions}
                label="Registry"
                placeholder="Select Registry"
                value={selectedRegistryId ?? null}
                onChange={handleRegistryChange}
            />
            <Select
                clearable
                withAsterisk
                comboboxProps={{ withinPortal: false }}
                data={moduleOptions}
                disabled={!selectedRegistryId}
                label="Module"
                placeholder={
                    selectedRegistryId
                        ? 'Select module'
                        : 'Select registry first'
                }
                value={selectedModuleIdn ?? null}
                onChange={handleModuleChange}
            />
            <Select
                clearable
                withAsterisk
                comboboxProps={{ withinPortal: false }}
                data={versionOptions}
                disabled={!selectedModuleIdn}
                label="Version"
                placeholder={
                    selectedModuleIdn ? 'Select version' : 'Select module first'
                }
                value={selectedVersionId ?? null}
                onChange={(value) => {
                    setSelectedVersionId(value ?? undefined);
                }}
            />
            <div className="flex justify-between gap-2">
                <UnstyledButton
                    className="text-xs text-gray-6"
                    onClick={onCancel}
                >
                    Cancel
                </UnstyledButton>
                <Button
                    disabled={!selection || isSubmitDisabled}
                    loading={isPending}
                    size="xs"
                    type="submit"
                >
                    {submitLabel}
                </Button>
            </div>
        </form>
    );
};

export default RegistryModuleForm;
